import { Briefcase } from '@ankr.com/ui';
import { Skeleton, Typography } from '@mui/material';
import { t } from '@ankr.com/common';
import { useMemo } from 'react';

import { ChargingModelType } from 'modules/billing/types';
import { Placeholder } from 'modules/common/components/Placeholder';
import { PromoBadge } from 'modules/common/components/PromoBadge';
import { usePartnerClientStatus } from 'domains/jwtToken/hooks/usePartnerClientStatus';

import { getLabel } from './UserLabelUtils';
import { useUserLabelStyles } from './useUserLabelStyles';

export interface IUserLabelProps {
  chargingModelType?: ChargingModelType;
  className?: string;
  hasEnterpriseStatus?: boolean;
  hasPremium?: boolean;
  hasStatusTransition?: boolean;
  isLoading?: boolean;
  isPromo?: boolean;
  keepSuffix?: boolean;
  size: 'small' | 'medium' | 'large';
  suffix?: string;
}

export const UserLabel = ({
  chargingModelType,
  className: nestedClassName,
  hasEnterpriseStatus = false,
  hasPremium = false,
  hasStatusTransition = false,
  isLoading,
  isPromo,
  keepSuffix = false,
  size = 'medium',
  suffix = '',
}: IUserLabelProps) => {
  const { isPartnerClient } = usePartnerClientStatus();

  const { classes, cx } = useUserLabelStyles();

  const hasPremiumLabel = hasStatusTransition ? !hasPremium : hasPremium;

  const className = cx(
    classes.root,
    {
      [classes.premium]: hasPremium,
      [classes.freemium]: !hasPremium,
      [classes.transition]: hasStatusTransition || isPartnerClient,
      [classes.enterprise]: hasEnterpriseStatus,
      [classes.package]: chargingModelType === 'package',
      [classes.deal]: chargingModelType === 'deal',
      [classes.small]: size === 'small',
      [classes.large]: size === 'large',
    },
    nestedClassName,
  );

  const label = useMemo(
    () => getLabel(hasPremiumLabel, chargingModelType),
    [chargingModelType, hasPremiumLabel],
  );

  if (isLoading) {
    return (
      <Skeleton
        className={cx(classes.root, classes.skeleton, nestedClassName)}
        variant="rectangular"
        width={70}
        height={24}
      />
    );
  }

  if (isPromo) {
    return <PromoBadge className={nestedClassName} />;
  }

  const isSmall = size === 'small';

  return (
    <Typography className={className} variant={isSmall ? 'body4' : 'body2'}>
      <Placeholder
        hasPlaceholder={!hasEnterpriseStatus}
        placeholder={keepSuffix ? suffix : label + suffix}
      >
        <>
          <Briefcase fontSize="small" className={classes.enterpriseIcon} />
          {t('chains.user-enterprise')}
        </>
      </Placeholder>
    </Typography>
  );
};
